import { createAsyncThunk } from '@reduxjs/toolkit';
import { HttpResponse, http, httpExternal } from '../../../helpers/request';
import { 
    GetProductsFromTransparencyCatalogAPI,
    GetProductsFilterResponse,
    GetLowEmittingTypesResponse 
} from './types';
import { BrandCompany, ExternalProduct } from '../../../models/product/types';
import { MasterFormatBackground } from '../../../models/product/enums';
import { ActionResponse } from '../../../models/actionResponse/types';
import { SustainabilityDocument } from '../../actions/productActions/types';
import { TCImpactArea } from '../../../types/impactAreas/impactAreas.types';
import { MasterFormatNode } from '../../../models/masterFormat/types';

const transparencyCatalogAPI = `${process.env.REACT_APP_TC_URL}/api/v1`;
const baseUrl = `/projects`;

// Max ids per Transparency Catalog request. Sending every id at once (large
// projects) overflows the request/response and TC returns 502, so requests are
// split into chunks fetched in parallel waves.
const TC_COMPANIES_CHUNK_SIZE = 100;
const TC_FETCH_CONCURRENCY = 4;
export const getExternalProductsAction = createAsyncThunk<
    HttpResponse<any>,
    GetProductsFromTransparencyCatalogAPI
>(
    'transparencyCatalogActions/getExternalProducts',
    async (data: GetProductsFromTransparencyCatalogAPI) => {
        console.log(data);
        console.log(data.productIds.join(','));
        console.log('TC API URL:');
        console.log(`${transparencyCatalogAPI}/products?ids=${data.productIds.join(',')}`);

        const result = await httpExternal({
            path: `${transparencyCatalogAPI}/products?ids=${data.productIds.join(',')}`,
            method: 'get',
        });
        console.log('GetProductDetails:');
        console.log(result);
        return result;
    },
);


export const getExternalProductsFromAllProjectsAction = createAsyncThunk<// (fetches project IDs first)
    GetProductsFromTransparencyCatalogAPIResponse | undefined,
    void
>(
    'transparencyCatalogActions/getProductsFromAllProjects',
    async (_, { rejectWithValue }) => {
        try {
            console.log('Fetching project IDs...');
            const projectIdsResult = await http<any,any>({
             path: `${baseUrl}/all-project-ids`,
                method: 'get',
            });
            
            const projectIds = projectIdsResult.parsedBody
            if (!projectIds || projectIds.length === 0) {
                console.log('No project IDs found');
                return { mfSection: [] };
            }
        
        console.log('Project IDs:', projectIds);
        console.log('Calling Transparency Catalog API...');
        console.log(`${transparencyCatalogAPI}/products?ids=${projectIds.join(',')}`); 
        const result = (await httpExternal({//Call external products API with the fetched IDs
            path: `${transparencyCatalogAPI}/products?ids=${projectIds.join(',')}`,
            method: 'get',
        })) as unknown as GetProductsFromTransparencyCatalogAPIResponse;
        
            console.log('GetProductDetails:');
            console.log(result);
            
            return result;
        } catch (error: any) {
            console.error('Error in getProductsFromAllProjectsAction:', error);
            if (error?.status === 405) {
                console.error('405 Method Not Allowed - Check if backend route is correct. It might need a prefix like /api/projects');
            }
            return rejectWithValue(error?.message || 'Failed to fetch products from all projects');
        }
    }
);

export const getBrandByIdAction = createAsyncThunk<HttpResponse<any>, number[]>(
    'transparencyCatalogActions/getBrandById',
    async (companyIds: Array<number>) => {
        const result = await httpExternal({
            path: `${transparencyCatalogAPI}/companies?ids=${companyIds.join(',')}`,
            method: 'get',
        });
        console.log('GetCompanyDetails:');
        console.log(result);
        return result;
    },
);

export const getCompaniesOfProducts = async (
    products: ExternalProduct[],
    dispatch: any,
): Promise<BrandCompany[]> => {
    const productsWithInvalidCompanyId = products.filter((product) => !Boolean(product.companyID));
    const fakeCompanies: BrandCompany[] = productsWithInvalidCompanyId.map((product) => ({
        id: -1,
        company: product.company,
        companyUrl: '',
        description: '',
        logo: { url: '', height: undefined, width: undefined },
        featuredProductImage: '',
        listingUrl: '',
        contactInfo: '',
        contactEmail: '',
        websiteUrl: '',
        level: MasterFormatBackground.STANDARD,
    }));

    const productsWithValidCompanyId = products.filter((product) => Boolean(product.companyID));
    if (!productsWithValidCompanyId.length) {
        return fakeCompanies;
    }

    const validCompanyIds = productsWithValidCompanyId.map((product) => product.companyID);
    const uniqueCompanyIds = [...new Set(validCompanyIds)];

    // Same TC size limit as /products: chunk the company ids and fetch in
    // parallel waves so large projects don't blow the request/response limit.
    const chunks: number[][] = [];
    for (let i = 0; i < uniqueCompanyIds.length; i += TC_COMPANIES_CHUNK_SIZE) {
        chunks.push(uniqueCompanyIds.slice(i, i + TC_COMPANIES_CHUNK_SIZE));
    }

    const fetchedCompanies: BrandCompany[] = [];
    for (let i = 0; i < chunks.length; i += TC_FETCH_CONCURRENCY) {
        const wave = chunks.slice(i, i + TC_FETCH_CONCURRENCY);
        const responses = (await Promise.all(
            wave.map((chunk) => dispatch(getBrandByIdAction(chunk))),
        )) as Array<ActionResponse<Array<BrandCompany>>>;
        for (const { payload } of responses) {
            if (payload?.data) {
                fetchedCompanies.push(...payload.data);
            }
        }
    }

    return [...fetchedCompanies, ...fakeCompanies];
};

export const getAllLowEmittingTypes = createAsyncThunk(
    'transparencyCatalogActions/getAllLowEmittingTypes',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/iaq`,
            method: 'get',
        }) as unknown) as GetLowEmittingTypesResponse;
    }
);

export const getRatingSystems = createAsyncThunk(
    'transparencyCatalogActions/getRatingSystems',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/ratingsystems`,
            method: 'get',
        }) as unknown) as GetProductsFilterResponse;
    },
);

export const getAllEpdsFiltersAction = createAsyncThunk<GetProductsFilterResponse>(
    'transparencyCatalogActions/getAllEpds',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/epds-all`,
            method: 'get',
        }) as unknown) as GetProductsFilterResponse;
    },
);

export const getSmEpdsFiltersAction = createAsyncThunk<GetProductsFilterResponse>(
    'transparencyCatalogActions/getSmEpds',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/epds-sm`,
            method: 'get',
        }) as unknown) as GetProductsFilterResponse;
    },
);

export const getEc3FiltersAction = createAsyncThunk<GetProductsFilterResponse>(
    'transparencyCatalogActions/getEc3',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/ec3`,
            method: 'get',
        }) as unknown) as GetProductsFilterResponse;
    },
);

export const getRegulationsFiltersAction = createAsyncThunk<GetProductsFilterResponse>(
    'transparencyCatalogActions/getRegulations',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/regulations`,
            method: 'get',
        }) as unknown) as GetProductsFilterResponse;
    },
);

export const getMaterialsFiltersAction = createAsyncThunk<GetProductsFilterResponse>(
    'transparencyCatalogActions/getMaterials',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/materials`,
            method: 'get',
        }) as unknown) as GetProductsFilterResponse;
    },
);

export const getLowEmittingFiltersAction = createAsyncThunk<GetProductsFilterResponse>(
    'transparencyCatalogActions/getLowEmitting',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/iaq`,
            method: 'get',
        }) as unknown) as GetProductsFilterResponse;
    },
);

export const getSustainabilityDocumentTypesAction = createAsyncThunk<SustainabilityDocument>(
    'transparencyCatalogActions/sustainability-docs',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/sustainability-docs`,
            method: 'get',
        }) as unknown) as SustainabilityDocument;
    }
);

export const getSustainabilityDocumentsByDisclosuresAction = createAsyncThunk<
    SustainabilityDocument,
    string[]
>(
    'transparencyCatalogActions/getSustainabilityDocumentsByDisclosures',
    async (disclosures: string[]) => {
        // Join the disclosures array with commas to create query parameter
        const disclosureTypes = disclosures.join(',');
        
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/sustainability-docs?disclosureType=${disclosureTypes}`,
            method: 'get',
        }) as unknown) as SustainabilityDocument;
    }
);

export const getAllImpactAreasFromTC = createAsyncThunk(
    'transparencyCatalogActions/getAllImpactAreas',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/sustainability-docs/impact-areas`,
            method: 'get',
        }) as unknown) as Array<TCImpactArea>;
    },
);

// hasProducts=1 returns what TC's own MasterFormat filter offers, which is what a saved search can select from.
export const getMasterFormatAction = createAsyncThunk<{ data: MasterFormatNode[] }>(
    'transparencyCatalogActions/getMasterFormat',
    async () => {
        return (await httpExternal({
            path: `${transparencyCatalogAPI}/masterformat?hasProducts=1`,
            method: 'get',
        }) as unknown) as { data: MasterFormatNode[] };
    },
);
